import { useEffect, useMemo, useRef, useState } from "react";

import {
    AppstoreOutlined,
    ArrowRightOutlined,
    CheckOutlined,
    CloseOutlined,
    InboxOutlined,
    KeyOutlined,
    MenuOutlined,
    SafetyCertificateOutlined,
    SearchOutlined,
    SettingOutlined,
} from "@ant-design/icons";
// itshover has no plain hamburger/key icon (its "toggle-icon" is a literal
// on/off switch, not a menu glyph), so MenuOutlined and KeyOutlined stay
// AntD rather than forcing an unrelated glyph in - same call as Calendar in
// the sidebar.
import {
    MagnifierIcon,
    RocketIcon,
    QuestionMark,
    FilledBellIcon,
    ShieldCheck,
    SparklesIcon,
    CheckedIcon,
    ChartLineIcon,
    MessageCircleIcon,
    BookIcon,
    BulbSvg,
    BugIcon,
} from "../Sidebar/icons/itshover";
import {
    Badge,
    Button,
    Divider,
    Dropdown,
    Drawer,
    Input,
    Segmented,
    Select,
    Skeleton,
    Tag,
    Tooltip,
} from "antd";
import type { MenuProps } from "antd";
import { Link, useNavigate } from "react-router-dom";

import NotificationItem from "../Notifications/NotificationItem";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { useMaxWidth } from "../../hooks/useMaxWidth";
import {
    deleteNotification,
    markNotificationsRead,
    updateNotification,
} from "../../redux/notifications/notification.slice";
import {
    isNotificationInOrgScope,
    isWithinLastDays,
    NOTIFICATION_DRAWER_DAYS,
} from "../../utils/notificationUtils";
import {
    fetchAllOrganizations,
    fetchAuditCategoriesByOrganizationId,
    fetchOrganizationById,
    fetchOrganizationsOfAdmin,
    fetchSubOrganizations,
    selectOrgPickerList,
    setSelectedAuditCategory,
    setSelectedOrganization,
    clearSubOrganizations,
} from "../../redux/organization/organization.slice";
import { openPalette } from "../../redux/search/search.slice";
import { openShortcuts } from "../../redux/ui/ui.slice";
import { toggleMobileSidebar } from "../../redux/sidebar/sidebar.slice";
import { RootState } from "../../redux/store";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";

import ProfileDropdown from "./ProfileDropdown";
import {
    NavContextSkeleton,
    NavIconSkeleton,
    NavProfileSkeleton,
} from "../Shimmers/AppShellSkeletons";
import { SelectOrganizations } from "./SelectOrganization";
import { OrganizationLogoMark } from "./OrganizationLogoMark";
import { SIDEBAR_DRAWER_MAX_WIDTH } from "../../constants/layout";
import { formatDateIST } from "../../utils/formatDateIST";
import "./Navbar.scss";

// ─── Constants ─────────────────────────────────────────────────────────
/** Mirrors "selectedOrganizationId" - the category had no persistence at all. */
const SELECTED_CATEGORY_KEY = "selectedAuditCategoryId";
const formatNotifCount = (count: number): string => {
    if (count > 100) return "99+";
    if (count > 50) return "50+";
    if (count > 10) return "9+";
    return count.toString();
};

const getShortcutHint = (): string => {
    const isMac =
        typeof navigator !== "undefined" &&
        /Mac|iPhone|iPad/.test(navigator.platform || "");
    return isMac ? "⌘K" : "Ctrl K";
};

const ndRelativeTime = (date: string | Date): string => {
    const then = new Date(date).getTime();
    if (Number.isNaN(then)) return "";
    const diff = Date.now() - then;
    const sec = Math.round(diff / 1000);
    if (sec < 45) return "just now";
    if (sec < 90) return "1m";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day}d`;
    const wk = Math.round(day / 7);
    if (wk < 5) return `${wk}w`;
    return formatDateIST(date);
};

const ndGroupKey = (date: string | Date): string => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sevenDays = new Date();
    sevenDays.setDate(today.getDate() - 7);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    if (d.getTime() >= sevenDays.getTime()) return "This week";
    return "Earlier";
};

type NDFilter = "all" | "unread";

// How many notification cards to render at once in the drawer. Keeps the DOM
// small (the drawer can otherwise render hundreds of cards at once); "Show
// more" reveals the next page.
const ND_PAGE_SIZE = 15;

// ─── Component ─────────────────────────────────────────────────────────
const Navbar = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isMobile = useMaxWidth(767);
    const isSidebarDrawer = useMaxWidth(SIDEBAR_DRAWER_MAX_WIDTH);

    // ── Auth + roles ──────────────────────────────────────────────────
    const admin = useAppSelector((s: RootState) => s.auth.adminMode);
    const owner = useAppSelector((s: RootState) => s.auth.ownerMode);
    const superAdmin = useAppSelector((s: RootState) => s.auth.superAdminMode);
    const p3mSuperAdmin = useAppSelector(
        (s: RootState) => s.auth.p3mSuperAdminMode
    );
    const isAuthenticated = useAppSelector(
        (s: RootState) => s.auth.isAuthenticated
    );
    const authUser = useAppSelector((s: RootState) => s.auth.user);
    const accessToken = useAppSelector((s: RootState) => s.auth.access_token);
    // Give up on the skeleton after a few seconds so a failed user-load can
    // never leave the navbar stuck loading.
    const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
    useEffect(() => {
        if (!accessToken || authUser) return;
        const t = setTimeout(() => setHydrationTimedOut(true), 6000);
        return () => clearTimeout(t);
    }, [accessToken, authUser]);
    // A session token exists but the user details haven't loaded yet; drives the
    // navbar element skeletons. Clears as soon as the user resolves.
    const shellLoading = !!accessToken && !authUser && !hydrationTimedOut;

    // ── Organizations + categories ────────────────────────────────────
    const allOrganizations = useAppSelector(
        (s) => s.organization.allOrganizations
    );
    const organizationsOfAdmin = useAppSelector(
        (s) => s.organization.organizationsOfAdmin
    );
    const userOrganizations = useAppSelector(
        (s) => s.auth.user?.organization
    );
    const selectedOrganization = useAppSelector(
        (s) => s.organization.selectedOrganization
    );
    const auditCategories = useAppSelector(
        (s) => s.organization.auditCategories
    );
    const selectedAuditCategory = useAppSelector(
        (s) => s.organization.selectedAuditCategory
    );
    const subOrganizations = useAppSelector(
        (s) => s.organization.subOrganizations
    );
    const loadedSubOrgsForMasterId = useAppSelector(
        (s) => s.organization.loadedSubOrgsForMasterId
    );
    const orgList = useAppSelector(selectOrgPickerList);
    const notifications = useAppSelector(
        (s) => s.notifications.notifications
    );
    const notificationSummary = useAppSelector(
        (s) => s.notifications.summary
    );

    // Scope the whole drawer to the selected org: notifications that belong to
    // it, plus untagged/global ones (lenient - nothing important disappears).
    const selectedOrgId = selectedOrganization?._id;
    const orgScopedNotifs = useMemo(
        () =>
            (notifications ?? [])
                .filter((n: any) =>
                    isNotificationInOrgScope(n.organization, selectedOrgId)
                )
                .filter((n: any) =>
                    isWithinLastDays(n.createdAt, NOTIFICATION_DRAWER_DAYS)
                ),
        [notifications, selectedOrgId]
    );

    // ── Derived state ─────────────────────────────────────────────────
    const unreadCount = notificationSummary.totalUnread;

    const effectiveOrgList = useMemo<any[]>(() => {
        if (!isAuthenticated && selectedOrganization) {
            return [selectedOrganization];
        }
        return orgList;
    }, [isAuthenticated, selectedOrganization, orgList]);

    const ensureSubOrganizationsForMaster = (org: any) => {
        if (!org?.isMasterOrganization || !org?._id) {
            dispatch(clearSubOrganizations());
            return;
        }
        if (loadedSubOrgsForMasterId !== org._id) {
            dispatch(fetchSubOrganizations(org._id));
        }
    };

    // ── Local UI state ────────────────────────────────────────────────
    const [openNotiDrawer, setOpenNotiDrawer] = useState(false);
    const [hasFetchedOrgs, setHasFetchedOrgs] = useState(false);
    const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);
    // Guards the one-off "fetch the saved org by id" in the restore effect below.
    // Without it, a saved org id that never lands in effectiveOrgList (e.g. a
    // sub-org of a different master) is refetched on every render — each fetch
    // mutates the store, re-runs this effect, and loops infinitely.
    const restoreFetchAttemptedRef = useRef<string | null>(null);
    const [ndFilter, setNdFilter] = useState<NDFilter>("all");
    const [ndQuery, setNdQuery] = useState("");
    const [ndVisible, setNdVisible] = useState(ND_PAGE_SIZE);

    // Reset paging whenever the list it pages over changes (org switch, tab,
    // search) or the drawer is (re)opened.
    useEffect(() => {
        setNdVisible(ND_PAGE_SIZE);
    }, [ndFilter, ndQuery, selectedOrgId, openNotiDrawer]);

    const shortcutHint = useMemo(getShortcutHint, []);

    // ── Effects ───────────────────────────────────────────────────────
    // Initial org fetch by role — lightweight masters-only list; sub-orgs load only
    // when the user selects a master org in the picker.
    useEffect(() => {
        if (hasFetchedOrgs) return;

        if (p3mSuperAdmin) {
            if (allOrganizations.length > 0) {
                setHasFetchedOrgs(true);
                return;
            }
            dispatch(
                fetchAllOrganizations({ mastersOnly: true, minimal: true })
            ).then((result) => {
                if (fetchAllOrganizations.fulfilled.match(result)) {
                    setHasFetchedOrgs(true);
                }
            });
            return;
        }

        if (admin || owner || superAdmin) {
            if (organizationsOfAdmin.length > 0) {
                setHasFetchedOrgs(true);
                return;
            }
            dispatch(
                fetchOrganizationsOfAdmin({ mastersOnly: true, minimal: true })
            ).then((result) => {
                if (fetchOrganizationsOfAdmin.fulfilled.match(result)) {
                    setHasFetchedOrgs(true);
                }
            });
            return;
        }

        if (
            userOrganizations?.length &&
            !selectedOrganization?._id
        ) {
            dispatch(setSelectedOrganization(userOrganizations[0]));
            setHasFetchedOrgs(true);
        }
    }, [
        p3mSuperAdmin,
        admin,
        superAdmin,
        owner,
        allOrganizations.length,
        organizationsOfAdmin.length,
        userOrganizations?.length,
        hasFetchedOrgs,
        dispatch,
    ]);

    // Restore selected org from localStorage once the list is available
    // (PRESERVED).
    useEffect(() => {
        if (effectiveOrgList.length === 0 && !localStorage.getItem("selectedOrganizationId")) {
            return;
        }

        const savedOrgId = localStorage.getItem("selectedOrganizationId");

        const resolveSavedOrg = async () => {
            let savedOrg = savedOrgId
                ? effectiveOrgList.find((org: any) => org._id === savedOrgId)
                : null;

            // Already on the saved org — nothing to fetch or restore.
            if (savedOrgId && !savedOrg && selectedOrganization?._id === savedOrgId) {
                savedOrg = selectedOrganization;
            }

            // Fetch the saved org by id at most once. It may never appear in
            // effectiveOrgList (fetchOrganizationById stores it in
            // selectedOrganization, not the picker list), so retrying would loop.
            if (
                savedOrgId &&
                !savedOrg &&
                restoreFetchAttemptedRef.current !== savedOrgId
            ) {
                restoreFetchAttemptedRef.current = savedOrgId;
                const result = await dispatch(fetchOrganizationById(savedOrgId));
                if (fetchOrganizationById.fulfilled.match(result)) {
                    savedOrg = result.payload;
                } else {
                    /*
                     * Stale id from another session/user. Fetching it used to
                     * succeed (get-organization had no tenant check) and then
                     * tenant-checked APIs 403'd. Drop it so we fall back to
                     * an org this account can actually see.
                     */
                    localStorage.removeItem("selectedOrganizationId");
                }
            }

            if (!hasRestoredFromStorage) {
                if (savedOrg) {
                    if (savedOrg.isMasterOrganization) {
                        ensureSubOrganizationsForMaster(savedOrg);
                    } else {
                        dispatch(clearSubOrganizations());
                    }
                    dispatch(setSelectedOrganization(savedOrg));
                } else if (effectiveOrgList[0]) {
                    if (effectiveOrgList[0].isMasterOrganization) {
                        ensureSubOrganizationsForMaster(effectiveOrgList[0]);
                    }
                    dispatch(setSelectedOrganization(effectiveOrgList[0]));
                    localStorage.setItem("selectedOrganizationId", effectiveOrgList[0]._id);
                }
                setHasRestoredFromStorage(true);
            } else if (!selectedOrganization?._id && effectiveOrgList[0]) {
                dispatch(setSelectedOrganization(effectiveOrgList[0]));
            }
        };

        resolveSavedOrg();
    }, [
        effectiveOrgList,
        selectedOrganization?._id,
        hasRestoredFromStorage,
        dispatch,
    ]);

    // Fetch audit categories when the org changes.
    useEffect(() => {
        if (selectedOrganization?._id) {
            dispatch(
                fetchAuditCategoriesByOrganizationId(selectedOrganization._id)
            );
        }
    }, [selectedOrganization?._id, dispatch]);

    /*
     * Keep a valid category selected as the list changes.
     *
     * This used to fall straight to `auditCategories[0]` whenever the current
     * selection was not in the list, which quietly discarded the user's choice
     * every time the list was replaced - and since the category was never
     * persisted, every full reload started from the first category again. The
     * organization had localStorage restore; the category had nothing.
     *
     * Now the stored id wins when it exists in the new list, so the choice
     * survives a reload, and switching away to another organization and back
     * returns you to the category you were working in.
     */
    useEffect(() => {
        if (auditCategories?.length > 0) {
            const stillValid = auditCategories.some(
                (c) => c._id === selectedAuditCategory?._id
            );
            if (!stillValid) {
                const savedId = localStorage.getItem(SELECTED_CATEGORY_KEY);
                const saved = savedId
                    ? auditCategories.find((c) => c._id === savedId)
                    : null;
                dispatch(setSelectedAuditCategory(saved ?? auditCategories[0]));
            }
        } else if (selectedAuditCategory) {
            dispatch(setSelectedAuditCategory(null));
        }
    }, [auditCategories, selectedAuditCategory, dispatch]);

    // ── Handlers ──────────────────────────────────────────────────────
    const handleSelectChange = (value: string) => {
        if (
            !isAuthenticated &&
            selectedOrganization &&
            selectedOrganization._id === value
        ) {
            return;
        }
        localStorage.setItem("selectedOrganizationId", value);
        const found =
            effectiveOrgList.find((org: any) => org._id === value) ||
            subOrganizations.find((org: any) => org._id === value);
        if (found) {
            if (found.isMasterOrganization) {
                ensureSubOrganizationsForMaster(found);
            } else {
                dispatch(clearSubOrganizations());
            }
            dispatch(setSelectedOrganization(found));
        } else {
            dispatch(fetchOrganizationById(value));
        }
    };

    const handleCategoryChange = (value: string) => {
        const found = auditCategories.find((c) => c._id === value);
        if (found) {
            /*
             * Persisted ONLY here, on an explicit pick.
             *
             * The auto-select effect above also dispatches
             * setSelectedAuditCategory, but writing there too would let a
             * fallback overwrite a real choice. Keeping the stored value tied
             * to intent is what lets the effect restore it rather than
             * defaulting to the first category.
             */
            localStorage.setItem(SELECTED_CATEGORY_KEY, found._id);
            dispatch(setSelectedAuditCategory(found));
        }
    };

    const handleMarkAsRead = (id: string) => {
        dispatch(updateNotification({ _id: id, body: { read: true } }));
    };

    const handleDelete = (id: string) => {
        dispatch(deleteNotification(id));
        minimalToast({
            dispatch,
            _id: id,
            title: "Notification Deleted",
            message: "The notification has been successfully deleted.",
            type: NotificationSeverity.ERROR,
            category: NotificationCategory.UPDATE,
            priority: NotificationPriority.LOW,
        });
    };

    const handleMarkAllAsRead = () => {
        if (!authUser?._id) return;
        dispatch(
            markNotificationsRead({
                userId: authUser._id,
                organization: selectedOrgId,
            })
        );
    };

    // ── Render helpers ────────────────────────────────────────────────
    const ParameterSettingsLink = () => (
        <Link to="/parameter-settings" className="nav-pill__footer-link">
            <SettingOutlined />
            <span>View All Audit Categories</span>
        </Link>
    );

    const renderCategorySelect = (placeholder: string) => (
        <Select
            showSearch
            allowClear
            popupMatchSelectWidth={false}
            classNames={{ popup: { root: "nav-context__select-dropdown" } }}
            styles={{ popup: { root: { maxWidth: "90vw" } } }}
            value={selectedAuditCategory?._id}
            onChange={handleCategoryChange}
            placeholder={placeholder}
            className="nav-context__select"
            variant="borderless"
            labelRender={({ label }) => (
                <span
                    className="nav-context__select-label"
                    title={typeof label === "string" ? label : undefined}
                >
                    {label}
                </span>
            )}
            filterOption={(input, option) =>
                (option?.label?.toString() || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
            }
            options={auditCategories.map((cat) => ({
                label: cat.name,
                value: cat._id,
            }))}
            popupRender={(menu) => (
                <>
                    {menu}
                    <Divider style={{ margin: "8px 0" }} />
                    <ParameterSettingsLink />
                </>
            )}
        />
    );

    const renderContextBreadcrumb = (opts: {
        showCategory: boolean;
        mobile?: boolean;
    }) =>
        shellLoading ? (
            <NavContextSkeleton showCategory={opts.showCategory} />
        ) : (
            <div className="nav-context">
                <div className="nav-context__chunk nav-context__chunk--org">
                    <span
                        className={`nav-context__icon${selectedOrganization ? " nav-context__icon--org" : ""}`}
                        aria-hidden
                    >
                        {selectedOrganization ? (
                            <OrganizationLogoMark
                                name={selectedOrganization.name}
                                logo={selectedOrganization.logoBase64 || selectedOrganization.logo}
                                size={20}
                            />
                        ) : (
                            <SafetyCertificateOutlined />
                        )}
                    </span>
                    <div className="nav-context__body">
                        <span className="nav-context__label">Organization</span>
                        <SelectOrganizations
                            organizations={effectiveOrgList}
                            selectedOrganizationId={selectedOrganization?._id}
                            handleSelectChange={handleSelectChange}
                            embedInNav
                        />
                    </div>
                </div>

                {opts.showCategory && (
                    <>
                        <span className="nav-context__separator" aria-hidden>
                            ⁄
                        </span>
                        <div className="nav-context__chunk nav-context__chunk--category">
                            <span
                                className="nav-context__icon nav-context__icon--muted"
                                aria-hidden
                            >
                                <AppstoreOutlined />
                            </span>
                            <div className="nav-context__body">
                                <span className="nav-context__label">Category</span>
                                {renderCategorySelect(
                                    opts.mobile ? "Select" : "Select category"
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );

    // ── Notifications drawer (editorial premium) ──────────────────────
    const ndDedupedNotifs = useMemo(
        () =>
            orgScopedNotifs.filter(
                (v: any, i: number, a: any[]) =>
                    a.findIndex((t: any) => t._id === v._id) === i
            ),
        [orgScopedNotifs]
    );

    const ndFilteredNotifs = useMemo(() => {
        const q = ndQuery.trim().toLowerCase();
        return ndDedupedNotifs.filter((n: any) => {
            if (ndFilter === "unread" && n.read) return false;
            if (!q) return true;
            const hay = `${n.title || ""} ${n.message || ""} ${n.category || ""
                }`.toLowerCase();
            return hay.includes(q);
        });
    }, [ndDedupedNotifs, ndFilter, ndQuery]);

    // Sort once (unread first, then newest first) so paging is stable.
    const ndSortedFiltered = useMemo(
        () =>
            [...ndFilteredNotifs].sort((a: any, b: any) => {
                if (!!a.read !== !!b.read) return a.read ? 1 : -1;
                return (
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                );
            }),
        [ndFilteredNotifs]
    );

    // Only render the first `ndVisible` cards; "Show more" reveals the rest.
    const ndVisibleNotifs = useMemo(
        () => ndSortedFiltered.slice(0, ndVisible),
        [ndSortedFiltered, ndVisible]
    );
    const ndHasMore = ndSortedFiltered.length > ndVisibleNotifs.length;

    const ndGrouped = useMemo(() => {
        if (!ndVisibleNotifs.length) return [] as Array<[string, any[]]>;
        const order = ["Today", "Yesterday", "This week", "Earlier"];
        const map = new Map<string, any[]>();
        ndVisibleNotifs.forEach((n: any) => {
            const key = ndGroupKey(n.createdAt);
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(n);
        });
        return order
            .filter((k) => map.has(k))
            .map((k) => [k, map.get(k)!] as [string, any[]]);
    }, [ndVisibleNotifs]);

    const ndRenderCard = (n: any) => (
        <NotificationItem
            key={n._id}
            notification={n}
            time={ndRelativeTime(n.createdAt)}
            variant="compact"
            onMarkRead={handleMarkAsRead}
            onDelete={handleDelete}
            onNavigate={() => setOpenNotiDrawer(false)}
            fallbackLink="/notifications"
        />
    );

    const ndTotalCount = ndDedupedNotifs.length;

    const renderNotificationsDrawer = () => (
        <Drawer
            className="nd"
            placement="right"
            onClose={() => setOpenNotiDrawer(false)}
            open={openNotiDrawer}
            width={isMobile ? "100%" : 440}
            closable={false}
            destroyOnClose={false}
        >
            <div className="nd__head">
                <div className="nd__title-row">
                    <div>
                        <h2 className="nd__title">Notifications</h2>
                        {/* One atomic status message rather than a bare
                            number in its own live region: a screen reader
                            hears "3 unread of 12" once, not "3" out of
                            nowhere every time the count moves. */}
                        <div
                            className="nd__count"
                            role="status"
                            aria-atomic="true"
                        >
                            {unreadCount > 0
                                ? `${unreadCount} unread of ${ndTotalCount}`
                                : `${ndTotalCount} notification${ndTotalCount === 1 ? "" : "s"}, all read`}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="nd__close"
                        onClick={() => setOpenNotiDrawer(false)}
                        aria-label="Close notifications"
                    >
                        <CloseOutlined />
                    </button>
                </div>

                <div className="nd__toolbar">
                    <Input
                        className="nd__search"
                        value={ndQuery}
                        onChange={(e) => setNdQuery(e.target.value)}
                        placeholder="Search notifications"
                        prefix={<SearchOutlined />}
                        allowClear
                        aria-label="Search notifications"
                    />
                    <Button
                        className="nd__mark-all"
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleMarkAllAsRead}
                        disabled={unreadCount === 0}
                    >
                        Mark all read
                    </Button>
                </div>

                <Segmented
                    className="nd__filter"
                    value={ndFilter}
                    onChange={(v) => setNdFilter(v as NDFilter)}
                    options={[
                        { value: "all", label: `All (${ndTotalCount})` },
                        { value: "unread", label: `Unread (${unreadCount})` },
                    ]}
                />
            </div>

            <div className="nd__body">
                {ndGrouped.length > 0 ? (
                    <>
                        {ndGrouped.map(([label, items]) => (
                            <section key={label} className="notif-group">
                                <div className="notif-group__head">
                                    <span className="notif-group__label">
                                        {label}
                                    </span>
                                    <span className="notif-group__rule" />
                                    <span className="notif-group__count">
                                        {items.length}
                                    </span>
                                </div>
                                <div className="notif-group__items">
                                    {items.map(ndRenderCard)}
                                </div>
                            </section>
                        ))}
                        {ndHasMore && (
                            <Button
                                className="nd__more"
                                onClick={() =>
                                    setNdVisible((v) => v + ND_PAGE_SIZE)
                                }
                            >
                                Show{" "}
                                {ndSortedFiltered.length -
                                    ndVisibleNotifs.length}{" "}
                                more
                            </Button>
                        )}
                    </>
                ) : (
                    <div className="notif-empty">
                        <div className="notif-empty__icon" aria-hidden="true">
                            <InboxOutlined />
                        </div>
                        <div className="notif-empty__title">
                            {ndQuery || ndFilter === "unread"
                                ? "Nothing matches"
                                : "You're all caught up"}
                        </div>
                        <div className="notif-empty__text">
                            {ndQuery || ndFilter === "unread"
                                ? "Try clearing your search, or switch to All to see the rest."
                                : "Audit alerts and updates will show up here as they arrive."}
                        </div>
                    </div>
                )}
            </div>

            <div className="nd__foot">
                <Link
                    to="/notifications"
                    className="nd__viewall"
                    onClick={() => setOpenNotiDrawer(false)}
                >
                    View all notifications
                    <ArrowRightOutlined />
                </Link>
            </div>
        </Drawer>
    );

    const notificationsButton = (
        <Tooltip title="Notifications">
            <button
                type="button"
                className="nav__icon-btn"
                onClick={() => setOpenNotiDrawer(true)}
                aria-label={
                    unreadCount > 0
                        ? `Notifications, ${unreadCount} unread`
                        : "Notifications"
                }
            >
                <Badge
                    count={
                        unreadCount > 0
                            ? formatNotifCount(unreadCount)
                            : 0
                    }
                    overflowCount={999}
                    offset={[2, -2]}
                    size="small"
                >
                    <FilledBellIcon size={16} />
                </Badge>
            </button>
        </Tooltip>
    );

    // ── Help menu (docs live at docs.p3m.ai) ──────────────────────────
    const openExternal = (url: string) =>
        window.open(url, "_blank", "noopener,noreferrer");

    const helpMenuItems: MenuProps["items"] = [
        {
            key: "shortcuts",
            icon: <KeyOutlined />,
            label: "Keyboard shortcuts",
            onClick: () => dispatch(openShortcuts()),
        },
        {
            key: "docs",
            icon: <BookIcon size={16} />,
            label: "Documentation",
            onClick: () => openExternal("https://docs.p3m.ai/getting-started"),
        },
        {
            key: "whatsnew",
            icon: <BulbSvg size={16} />,
            label: "What's new",
            onClick: () => openExternal("https://docs.p3m.ai/changelog"),
        },
        { type: "divider" },
        {
            key: "report",
            icon: <BugIcon size={16} />,
            label: "Report an issue",
            onClick: () => navigate("/tickets"),
        },
    ];

    // ── Global "+ Create" menu (role-gated) ───────────────────────────
    const anyAdmin = admin || superAdmin || p3mSuperAdmin;
    const roles: string[] = Array.isArray(authUser?.role) ? authUser.role : [];
    const isEvaluator = roles.includes("Evaluator");

    const createMenuItems: MenuProps["items"] = [
        (anyAdmin || isEvaluator) && {
            key: "audit",
            icon: <ShieldCheck size={16} />,
            label: "Audit",
            // Opens the Schedule Audit tab and auto-opens its modal.
            onClick: () => navigate("/audits/3?schedule=1"),
        },
        (anyAdmin || isEvaluator) && {
            key: "assessment",
            icon: <SparklesIcon size={16} />,
            label: "Assessment",
            onClick: () => navigate("/assessments/create"),
        },
        {
            key: "task",
            icon: <CheckedIcon size={16} />,
            label: "Task",
            onClick: () =>
                navigate(
                    admin
                        ? "/task-and-goals?create=1"
                        : "/user-task-and-goals?create=1"
                ),
        },
        (anyAdmin || isEvaluator) && {
            key: "evaluation",
            icon: <ChartLineIcon size={16} />,
            label: "Evaluation",
            onClick: () => navigate("/evaluations?create=1"),
        },
        anyAdmin && {
            key: "notice",
            icon: <MessageCircleIcon size={16} />,
            label: "Notice",
            onClick: () => navigate("/notice?create=1"),
        },
    ].filter(Boolean) as MenuProps["items"];

    // ── Render ────────────────────────────────────────────────────────
    // Single breakpoint unified with the sidebar: at/below the drawer width the
    // navbar goes compact (hamburger + a second row for the context breadcrumb);
    // above it the bar is one row with search grid-centered to the viewport.
    const isCompact = isSidebarDrawer;

    return (
        <header className="nav">
            {/* Row 1 - context · search · actions */}
            <div className={`nav__row${isCompact ? "" : " nav__row--desktop"}`}>
                {isCompact && (
                    <Tooltip title="Menu">
                        <button
                            type="button"
                            className="nav__icon-btn nav__menu-btn"
                            onClick={() => dispatch(toggleMobileSidebar())}
                            aria-label="Open navigation menu"
                        >
                            <MenuOutlined />
                        </button>
                    </Tooltip>
                )}

                {!isCompact && (
                    <div className="nav__context">
                        {renderContextBreadcrumb({ showCategory: true })}
                    </div>
                )}

                <div
                    className={`nav__center${isCompact ? " nav__center--compact" : ""}`}
                >
                    {isMobile ? (
                        <Tooltip title="Search">
                            <button
                                type="button"
                                className="nav__icon-btn"
                                onClick={() => dispatch(openPalette())}
                                aria-label="Open search"
                            >
                                <MagnifierIcon size={16} />
                            </button>
                        </Tooltip>
                    ) : (
                        <button
                            type="button"
                            className="nav__search-trigger"
                            onClick={() => dispatch(openPalette())}
                            aria-label="Open search palette"
                        >
                            <MagnifierIcon size={16} className="nav__search-icon" />
                            <span className="nav__search-placeholder">
                                Search records, audits, people…
                            </span>
                            <Tag className="nav__search-shortcut">
                                {shortcutHint}
                            </Tag>
                        </button>
                    )}
                </div>

                <div className="nav__right">
                    {createMenuItems && createMenuItems.length > 0 && (
                        <Dropdown
                            menu={{ items: createMenuItems }}
                            trigger={["click"]}
                            placement="bottomRight"
                            rootClassName="nav-dropdown"
                            arrow
                        >
                            <Tooltip title="Create">
                                <button
                                    type="button"
                                    className="nav__icon-btn nav__icon-btn--accent"
                                    aria-label="Create new"
                                >
                                    <RocketIcon size={16} />
                                </button>
                            </Tooltip>
                        </Dropdown>
                    )}

                    <Dropdown
                        menu={{ items: helpMenuItems }}
                        trigger={["click"]}
                        placement="bottomRight"
                        rootClassName="nav-dropdown"
                        arrow
                    >
                        <Tooltip title="Help">
                            <button
                                type="button"
                                className="nav__icon-btn"
                                aria-label="Help and keyboard shortcuts"
                            >
                                <QuestionMark size={16} />
                            </button>
                        </Tooltip>
                    </Dropdown>

                    {shellLoading ? <NavIconSkeleton /> : notificationsButton}

                    {shellLoading ? <NavProfileSkeleton /> : <ProfileDropdown />}
                </div>
            </div>

            {/* Row 2 - compact (tablet/mobile) context breadcrumb */}
            {isCompact && (
                <div className="nav__row nav__row--secondary">
                    {renderContextBreadcrumb({
                        showCategory: true,
                        mobile: isMobile,
                    })}
                </div>
            )}

            {renderNotificationsDrawer()}
        </header>
    );
};

export default Navbar;
